import { useRef } from 'react';
import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

/**
 * v4 segmented control (DESIGN_SYSTEM.md section 8.9, #1377 board): a compact
 * exclusive choice between two to four short options (pay frequency, funding
 * mode, Entry Type). The track is a recessed strip; pass `inCard` when the
 * control sits on an already-recessed or overlay surface so the track lifts
 * instead. The active segment reads on surface-2 at medium weight with a 1px
 * inset ring in the interactive color (#1938: the fill alone did not read as
 * selected).
 *
 * An option may carry a `tone` ({ fg, bg }): the Payees page's abbreviated
 * Entry Type options (#1938). A toned segment shows its letter in the tone,
 * dimmed until chosen; the chosen one takes the tone's tint and a 1px ring in
 * the tone. Options without a tone keep the neutral look above.
 *
 * Radiogroup semantics: each segment is a radio button, so tests and assistive
 * tech address segments by role + accessible name. Roving tabindex: the group
 * is one Tab stop and the arrow keys move the selection. `disabled` renders
 * the locked look (muted ring, no change); with `greyLocked` that look is all
 * grey: tones dropped, the ring in text.dim (#2216).
 *
 * Preview (#1943, owner 2026-09-30): with `onPreview`, the ring follows the
 * mouse and the keyboard focus instead of the selection. Hovering or focusing
 * a segment reports its value; leaving the group reports null; the host draws
 * the ring on `previewValue` (falling back to `value`) and updates its caption
 * from it. Arrow keys then move focus only, and Enter or Space picks, so the
 * caption can be read before the choice is made. `aria-checked` stays on the
 * real value throughout.
 *
 * @param {object} props
 * @param {Array<string|{value: string, label: React.ReactNode, title?: string, ariaLabel?: string, tone?: {fg: string, bg: string}}>} props.options
 * @param {string} props.value            the active option value
 * @param {function} [props.onChange]     called with the newly selected value
 * @param {string} [props.previewValue]   the option the ring sits on while previewing
 * @param {function} [props.onPreview]    hover or focus reports a value, leaving reports null
 * @param {boolean} [props.inCard]        raised track for recessed/overlay hosts
 * @param {boolean} [props.disabled]      locked: no change, muted ring
 * @param {boolean} [props.greyLocked]    with `disabled`: the locked look in grey only
 * @param {string} props.ariaLabel        names the group (no visible label)
 * @param {object} [props.sx]             extra sx merged onto the track
 */
const Segmented = ({ options, value, onChange, previewValue, onPreview, inCard = false, disabled = false, greyLocked = false, ariaLabel, sx }) => {
  const theme = useTheme();
  const { ds, palette } = theme;
  const buttonRefs = useRef([]);
  const groupRef = useRef(null);
  const items = options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt));
  const activeIndex = items.findIndex((o) => o.value === value);
  const previewing = typeof onPreview === 'function';
  const shownValue = previewing && previewValue != null ? previewValue : value;
  const grey = disabled && greyLocked;
  let ring = palette.interactive;
  if (grey) ring = palette.text.dim;
  else if (disabled) ring = alpha(palette.interactive, 0.4);

  const move = (from, delta) => {
    if (disabled || items.length === 0) return;
    const next = (from + delta + items.length) % items.length;
    // Previewing: the arrow moves focus (and so the ring); the pick waits for
    // Enter or Space. Otherwise the arrow is the pick, as a radiogroup's is.
    if (!previewing && onChange) onChange(items[next].value);
    buttonRefs.current[next]?.focus();
  };
  const leave = (e) => {
    if (!previewing) return;
    // A blur that stays inside the group (arrow focus) keeps the preview.
    if (e.type === 'blur' && groupRef.current?.contains(e.relatedTarget)) return;
    onPreview(null);
  };

  return (
    <Box
      ref={groupRef}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      onMouseLeave={leave}
      onBlur={leave}
      sx={{
        display: 'flex',
        backgroundColor: inCard ? palette.surface.raised : palette.surface.recessed,
        border: `1px solid ${palette.divider}`,
        borderRadius: '5px',
        padding: '2px',
        width: 'fit-content',
        flexShrink: 0,
        ...sx,
      }}
    >
      {items.map((o, index) => {
        const checked = o.value === value;
        const active = o.value === shownValue;
        const tabStop = activeIndex === -1 ? index === 0 : checked;
        const tone = grey ? undefined : o.tone;
        let color;
        if (tone) color = disabled ? (active ? palette.text.secondary : palette.text.dim) : tone.fg;
        else color = disabled ? palette.text.dim : (active ? palette.text.primary : palette.text.secondary);
        let shadow = 'none';
        if (active) shadow = `inset 0 0 0 1px ${tone ? (disabled ? alpha(tone.fg, 0.35) : tone.fg) : ring}`;
        // A toned letter reads dimmed until chosen, brighter on hover.
        const dimmed = Boolean(tone) && !active && !disabled;
        return (
          <Box
            key={o.value}
            component="button"
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={o.ariaLabel}
            title={o.title}
            disabled={disabled}
            tabIndex={tabStop ? 0 : -1}
            ref={(el) => { buttonRefs.current[index] = el; }}
            onMouseEnter={previewing && !disabled ? () => onPreview(o.value) : undefined}
            onFocus={previewing && !disabled ? () => onPreview(o.value) : undefined}
            onClick={() => {
              if (!disabled && onChange && (previewing || !checked)) onChange(o.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                move(index, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                move(index, -1);
              }
            }}
            sx={{
              padding: '5px 14px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              border: 0,
              borderRadius: '4px',
              cursor: disabled ? 'default' : 'pointer',
              backgroundColor: active ? (tone ? tone.bg : palette.surface.hover) : 'transparent',
              boxShadow: shadow,
              fontWeight: active ? ds.font.weightMedium : ds.font.weightRegular,
              // Idle reads in text.secondary (the design system's --text-muted). The
              // theme has no text.muted key; asking for it left the browser's dark
              // button text on a dark track, so unselected options vanished (#1938).
              color,
              opacity: dimmed ? 0.55 : 1,
              ...(dimmed ? { '&:hover': { opacity: 0.85 } } : {}),
              transition: `background-color ${ds.motion.fast} ${ds.motion.easeOut}, color ${ds.motion.fast} ${ds.motion.easeOut}, box-shadow ${ds.motion.fast} ${ds.motion.easeOut}, opacity ${ds.motion.fast} ${ds.motion.easeOut}`,
            }}
          >
            {o.label}
          </Box>
        );
      })}
    </Box>
  );
};

export default Segmented;
